import { useReactFlow } from '@xyflow/react'
import { useCallback, useEffect, useRef } from 'react'

import { JumpToSwitchboardNodeData } from '../../../converters/shared/reactFlowTypes'

/**
 * Hook for Jump To Switchboard node functionality.
 * Handles switchboard selection.
 * Note: Available switchboards are read from extensionsListAtom (Jotai atom) by the SwitchboardSelector component.
 */
export const useJumpToSwitchboardNode = (
  id: string,
  data: JumpToSwitchboardNodeData,
) => {
  const { updateNodeData } = useReactFlow()
  const dataRef = useRef(data)

  // Keep dataRef in sync with data
  useEffect(() => {
    dataRef.current = data
  }, [data])

  const currentSwitchboardId = data.targetSwitchboardId || ''

  // Note: We do NOT auto-select the first switchboard to match legacy behavior.
  // Legacy allows dial_plan_id to be null until user explicitly selects a switchboard.
  // This ensures consistency when loading legacy nodes that may have null values.

  const handleSwitchboardChange = useCallback(
    (switchboardId: string | undefined) => {
      updateNodeData(id, {
        ...dataRef.current,
        targetSwitchboardId: switchboardId,
      })
    },
    [id, updateNodeData],
  )

  return {
    currentSwitchboardId,
    handleSwitchboardChange,
    hasData: !!currentSwitchboardId,
  }
}
